import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MdNotificationsActive, MdSave, MdSend } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import RecipientListEditor from './RecipientListEditor';
import TestEmailResults from './TestEmailResults';

const QUICK_MINUTES = [15, 30, 70, 120];

export default function CompanyAlertSettingsPanel({ companyId }) {
    const { t } = useLanguage();
    const [settings, setSettings] = useState(null);
    const [staleMinutes, setStaleMinutes] = useState('');
    const [extraRecipients, setExtraRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResults, setTestResults] = useState(null);

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/companies/${companyId}/alert-settings`);
            const data = res.data.data;
            setSettings(data);
            setStaleMinutes(data.staleMinutes ?? '');
            setExtraRecipients(data.extraRecipients || []);
        } catch {
            toast.error(t('alertSettings.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [companyId, t]);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleSave = async () => {
        if (staleMinutes !== '') {
            const m = parseInt(staleMinutes, 10);
            if (!Number.isFinite(m) || m < settings.minStaleMinutes || m > settings.maxStaleMinutes) {
                toast.error(t('alertSettings.invalidMinutes', { min: settings.minStaleMinutes, max: settings.maxStaleMinutes }));
                return;
            }
        }
        try {
            setSaving(true);
            await api.put(`/companies/${companyId}/alert-settings`, {
                staleMinutes: staleMinutes === '' ? null : parseInt(staleMinutes, 10),
                extraRecipients
            });
            toast.success(t('alertSettings.saved'));
            fetchSettings();
        } catch (err) {
            toast.error(err.response?.data?.message || t('alertSettings.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            setTestResults(null);
            const res = await api.post(`/companies/${companyId}/alert-settings/test`);
            setTestResults(res.data.data);
            const failed = res.data.data.filter(r => !r.success).length;
            failed === 0 ? toast.success(t('alertSettings.testSent')) : toast.error(t('alertSettings.testPartial', { count: failed }));
        } catch (err) {
            toast.error(err.response?.data?.message || t('alertSettings.testFailed'));
        } finally {
            setTesting(false);
        }
    };

    const savedExtras = JSON.stringify(settings?.extraRecipients || []);
    const dirty = settings && (
        String(staleMinutes) !== String(settings.staleMinutes ?? '') || JSON.stringify(extraRecipients) !== savedExtras
    );
    const userRecipients = (settings?.recipients || []).filter(r => r.source === 'user');

    return (
        <motion.div className="card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
            <div className="card-header">
                <div className="card-title">
                    <div className="card-title-icon" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>
                        <MdNotificationsActive />
                    </div>
                    {t('alertSettings.title')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={handleTest} disabled={loading || testing || dirty}
                        title={dirty ? t('alertSettings.saveBeforeTest') : ''}>
                        {testing ? <span className="spinner spinner-sm" /> : <MdSend />} {t('alertSettings.sendTest')}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={loading || saving || !dirty}>
                        {saving ? <span className="spinner spinner-sm" /> : <MdSave />} {t('common.save')}
                    </button>
                </div>
            </div>

            {loading || !settings ? (
                <div className="loading-container"><div className="spinner"></div></div>
            ) : (
                <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                    {/* Kesinti süresi */}
                    <div>
                        <label className="form-label">{t('alertSettings.staleMinutes')}</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <input
                                className="form-input"
                                type="number"
                                min={settings.minStaleMinutes}
                                max={settings.maxStaleMinutes}
                                value={staleMinutes}
                                placeholder={String(settings.defaultStaleMinutes)}
                                onChange={(e) => setStaleMinutes(e.target.value)}
                                style={{ width: 120 }}
                            />
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('alertSettings.minutes')}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {QUICK_MINUTES.map(m => (
                                <button key={m}
                                    className={`btn btn-sm ${String(staleMinutes || settings.defaultStaleMinutes) === String(m) ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setStaleMinutes(m === settings.defaultStaleMinutes ? '' : m)}
                                    style={{ height: 28, padding: '0 10px', fontSize: 12 }}>
                                    {m} {t('alertSettings.min')}
                                </button>
                            ))}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            {t('alertSettings.staleHint')}
                        </div>
                    </div>

                    {/* Ek alıcılar */}
                    <div>
                        <label className="form-label">{t('alertSettings.extraRecipients')}</label>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{t('alertSettings.extraRecipientsHint')}</div>
                        <RecipientListEditor value={extraRecipients} onChange={setExtraRecipients} disabled={saving} />
                    </div>

                    {/* Kullanıcı alıcıları */}
                    <div>
                        <label className="form-label">{t('alertSettings.userRecipients')}</label>
                        {userRecipients.length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('alertSettings.noUserRecipients')}</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {userRecipients.map(r => (
                                    <div key={r.email} style={{ fontSize: 13 }}>
                                        {r.email} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({r.language.toUpperCase()})</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{t('alertSettings.userRecipientsHint')}</div>
                        <TestEmailResults results={testResults} />
                    </div>
                </div>
            )}
        </motion.div>
    );
}
